import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType,
} from 'react-router-dom';
import { useMount } from 'react-use';

import { getTelemetryProfile, withoutSentryPii, withoutSentrySpanPii } from '@bedc/utils';

import { log } from '@bedc/client/utils';

import { environment } from '../environments/environment';

declare const BUILD_TIMESTAMP: string;

/** Whether this deployment uses Sentry, and whether it may send PII. */
const { sentry, sendPii } = getTelemetryProfile(environment.name);

export const SentryProvider = ({ children }: { children: React.ReactNode }) => {
	// State
	// ------------------------------------------------------------------------

	/** Flags. */
	const [initialized, setInitialized] = useState(false);

	// Life Cycle
	// ------------------------------------------------------------------------

	/**
	 * Initialize Sentry on load.
	 */
	useMount(() => {
		// Prevent duplicated initializations. (e.g. HMR)
		if (initialized) return;

		Sentry.init({
			dsn: environment.sentry.dsn,
			enabled: !!sentry && environment.sentry.enabled,
			environment: environment.name,
			release: BUILD_TIMESTAMP,
			integrations: [
				Sentry.reactRouterV6BrowserTracingIntegration({
					// Track from these URLs.
					// tracePropagationTargets: [
					// 	'localhost',
					// 	/^https:\/\/(.*.)?rome.bedc.ai/,
					// ],

					// Hook into react-router to track navigation.
					useEffect,
					useLocation,
					useNavigationType,
					createRoutesFromChildren,
					matchRoutes,
				}),

				// A replay shows everything on screen, unmasked.
				...(sendPii
					? [
							Sentry.replayIntegration({
								maskAllText: false,
								maskAllInputs: false,
							}),
						]
					: []),
			],

			...(sendPii
				? {}
				: {
						beforeSend: withoutSentryPii,
						beforeSendTransaction: withoutSentryPii,
						beforeSendSpan: withoutSentrySpanPii,
					}),

			// Percentage of errors to send.
			sampleRate: environment.sentry.sampleRate ?? 1.0,

			// Percentage of transactions to capture for Performance Monitoring.
			tracesSampleRate: environment.sentry.tracesSampleRate ?? 0.1, // Reduce in production!

			// Percentage of all sessions to capture for Session Replay.
			replaysSessionSampleRate: environment.sentry.replaysSessionSampleRate ?? 0.1, // Reduce in production!

			// Percentage of sessions with an error to capture for Session Replay.
			replaysOnErrorSampleRate: environment.sentry.replaysOnErrorSampleRate ?? 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
		});

		log.debug('Sentry', 'Initialized', { dsn: environment.sentry.dsn });

		setInitialized(true);
	});

	// Template
	// ------------------------------------------------------------------------

	// TODO: Nice error UI.
	return (
		<Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
			{children}
		</Sentry.ErrorBoundary>
	);
};
